import { fetchUtils } from 'react-admin';
import { getAuthToken } from './authProvider';

const API_BASE_URL = 'https://api.magtuner.com/admin';

// Custom httpClient that adds auth headers
const httpClient = (url, options = {}) => {
  const token = getAuthToken();

  if (!token) {
    return Promise.reject(new Error('No authentication token found'));
  }

  // Create a proper Headers instance
  const headers = new Headers({
    'Authorization': `Token token="${token}"`,
    'Content-Type': 'application/json',
    ...options.headers,
  });

  return fetchUtils.fetchJson(url, { ...options, headers });
};

export const dataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };

    // Build query parameters
    const query = {
      page: page,
      per_page: perPage,
      sort: `${field} ${order}`,
      ...flattenFilters(params.filter),
    };

    const url = `${API_BASE_URL}/${resource}?${new URLSearchParams(query)}`;

    try {
      const { headers, json } = await httpClient(url);

      // Handle both response formats: { resource: [...] } or [...]
      const data = Array.isArray(json) ? json : (json[resource] || []);

      // Read total count from JSON response (new API format)
      const totalCount = parseInt(json.total || '0', 10);

      console.log('totalCount from JSON:', totalCount, 'data length:', data.length);

      // If no total in JSON, assume enough records for pagination
      const finalTotal = totalCount || (data.length === perPage ? perPage * 10 : data.length);

      return {
        data: data,
        total: finalTotal,
      };
    } catch (error) {
      console.error('getList error:', error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const url = `${API_BASE_URL}/${resource}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      // For single resources, API returns singular key (e.g., "user" not "users")
      const singularResource = resource.slice(0, -1); // Remove 's' from plural
      const data = json[singularResource] || json[resource] || json;
      return {
        data: data,
      };
    } catch (error) {
      console.error('getOne error:', error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    const query = {
      ids: params.ids.join(','),
    };
    const url = `${API_BASE_URL}/${resource}?${new URLSearchParams(query)}`;

    try {
      const { json } = await httpClient(url);
      // Handle both response formats: { resource: [...] } or [...]
      const data = Array.isArray(json) ? json : (json[resource] || []);
      return {
        data: data,
      };
    } catch (error) {
      console.error('getMany error:', error);
      throw error;
    }
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };

    const query = {
      page: page,
      per_page: perPage,
      sort: `${field} ${order}`,
      [`q[${params.target}_eq]`]: params.id,
      ...flattenFilters(params.filter),
    };

    const url = `${API_BASE_URL}/${resource}?${new URLSearchParams(query)}`;

    try {
      const { headers, json } = await httpClient(url);
      // Handle both response formats: { resource: [...] } or [...]
      const data = Array.isArray(json) ? json : (json[resource] || []);
      return {
        data: data,
        total: parseInt(headers.get('X-Total-Count') || '0', 10),
      };
    } catch (error) {
      console.error('getManyReference error:', error);
      throw error;
    }
  },

  create: async (resource, params) => {
    const url = `${API_BASE_URL}/${resource}`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify({ [resource.slice(0, -1)]: params.data }),
      });

      // For create responses, API returns singular key (e.g., "user" not "users")
      const singularResource = resource.slice(0, -1);
      const data = json[singularResource] || json[resource] || json;
      return {
        data: data,
      };
    } catch (error) {
      console.error('create error:', error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const url = `${API_BASE_URL}/${resource}/${params.id}`;

    try {
      // Remove id from data as it shouldn't be updated
      const { id, ...dataToSend } = params.data;

      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify({ [resource.slice(0, -1)]: dataToSend }),
      });

      // For update responses, API returns singular key (e.g., "user" not "users")
      const singularResource = resource.slice(0, -1);
      const data = json[singularResource] || json[resource] || json;
      return {
        data: data,
      };
    } catch (error) {
      console.error('update error:', error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      const responses = await Promise.all(
        params.ids.map(id =>
          httpClient(`${API_BASE_URL}/${resource}/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ [resource.slice(0, -1)]: params.data }),
          })
        )
      );

      return {
        data: responses.map(({ json }) => (json[resource] || json).id),
      };
    } catch (error) {
      console.error('updateMany error:', error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const url = `${API_BASE_URL}/${resource}/${params.id}`;

    try {
      await httpClient(url, {
        method: 'DELETE',
      });

      return {
        data: { id: params.id },
      };
    } catch (error) {
      console.error('delete error:', error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${API_BASE_URL}/${resource}/${id}`, {
            method: 'DELETE',
          })
        )
      );

      return {
        data: params.ids,
      };
    } catch (error) {
      console.error('deleteMany error:', error);
      throw error;
    }
  },
};

// Helper function to flatten filters for Ransack
function flattenFilters(filter) {
  if (!filter) return {};

  const flattened = {};

  Object.keys(filter).forEach(key => {
    if (typeof filter[key] === 'object' && filter[key] !== null) {
      Object.keys(filter[key]).forEach(subKey => {
        flattened[`q[${key}_${subKey}]`] = filter[key][subKey];
      });
    } else {
      flattened[`q[${key}_eq]`] = filter[key];
    }
  });

  return flattened;
}
